import { isEqual } from 'lodash/fp';

import {
  LinkedWorkflowStep,
  WorkflowVersionMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import {
  CombinedVersionDiffStepForTree,
  Connector,
  ConnectorsDiff,
  StepsArrayDiff,
  VersionDiffChangeType,
  WorkflowVersionArrayDiff,
  WorkflowVersionStepDiff,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/types';
import { WorkflowStepConditional } from 'dashboard/models/workflow-version';

export function getWorkflowTokenFromWorkflowStep(
  workflowStep: LinkedWorkflowStep,
  workflowVersionMap: WorkflowVersionMap,
): string | undefined {
  const workflowVersionId = workflowStep.workflowVersionId;
  const workflowToken = workflowVersionMap[workflowVersionId]?.relationships.workflow.data.id;
  if (workflowToken) {
    return serverTokenToClientToken(workflowToken);
  }
}

export function serverTokenToClientToken(serverToken: string): string {
  return serverToken.split('.')[0];
}

function getWorkflowStepDiff({
  workflowToken,
  workflowStepToken,
  workflowVersionArrayDiff,
}: {
  workflowToken: string;
  workflowStepToken: string;
  workflowVersionArrayDiff: WorkflowVersionArrayDiff;
}): WorkflowVersionStepDiff | undefined {
  const workflowVersionDiff = workflowVersionArrayDiff[workflowToken];
  if (workflowVersionDiff) {
    return workflowVersionDiff.changes['steps-config']?.[workflowStepToken];
  }
}

export function getWorkflowConditionalBranchChangeType({
  workflowToken,
  workflowConditionalStep,
  branchIndex,
  workflowVersionArrayDiff,
}: {
  workflowToken: string;
  workflowConditionalStep: WorkflowStepConditional;
  branchIndex: number;
  workflowVersionArrayDiff: WorkflowVersionArrayDiff;
}): VersionDiffChangeType | undefined {
  const workflowVersionStepDiff = getWorkflowStepDiff({
    workflowToken,
    workflowStepToken: workflowConditionalStep.token,
    workflowVersionArrayDiff,
  });

  const conditionalToken = workflowConditionalStep.conditionals[branchIndex]?.token;

  if (conditionalToken && workflowVersionStepDiff) {
    const conditionalDiff = workflowVersionStepDiff.changes.conditionals?.[conditionalToken];
    return conditionalDiff?.['change-type'];
  }
}

export function calculateTransitionComponentChangeType(
  stepArrayDiff: StepsArrayDiff,
  stepName: string,
  componentName: string,
): VersionDiffChangeType.ADDED | VersionDiffChangeType.REMOVED | undefined {
  const componentDiff = stepArrayDiff[stepName]?.changes.components[componentName];
  // We don't want to highlight transition components as modified since the sole purpose of
  // these cards is to show the transition config between steps and we ignore that in the diff.
  const changeType = componentDiff?.['change-type'];
  return changeType === VersionDiffChangeType.MODIFIED ? undefined : changeType;
}

function connectorIsInConnectorArray(connector: Connector, connectorArray: Connector[]) {
  return connectorArray.some((c) => isEqual(c, connector));
}

export function calculateConnectorChangeType(
  step: CombinedVersionDiffStepForTree,
  connectorDiff: ConnectorsDiff['base'] | ConnectorsDiff['target'],
): VersionDiffChangeType | undefined {
  const connector: Connector = {
    'from-step': step.parentStepName!,
    'to-step': step.token!,
    'workflow-or-component-token': step.workflowOrComponentToken!,
  };

  if (connectorIsInConnectorArray(connector, connectorDiff.modified)) {
    return VersionDiffChangeType.MODIFIED;
  } else if (
    'added' in connectorDiff &&
    connectorIsInConnectorArray(connector, connectorDiff.added)
  ) {
    return VersionDiffChangeType.ADDED;
  } else if (
    'removed' in connectorDiff &&
    connectorIsInConnectorArray(connector, connectorDiff.removed)
  ) {
    return VersionDiffChangeType.REMOVED;
  }
}
